import React, {useCallback, useMemo} from 'react';
import {FormikHelpers} from 'formik';
import {Form} from 'forms';
import {PageLayoutTwoVerticalCols} from 'layout/components';
import {IUploadScormFormBaseValues, mapUploadResourceScorm, uploadScormFormBaseValidationSchema} from 'admin/resources';
import {ScormContentHeader, ScormContentFooter, ScormContentMiddle, ScormContentStart} from './components';
import {FormNameEnum} from '../enums';
import {useUploadScorm} from './';
import {usePermissions} from 'permissions';
import {SuperAndNationalAdminroles} from 'enums/admin-roles';
import {useLanguages} from '../hooks';
import {usePrefetchResourceMetadataLookups} from 'filters-selections';
import {runResourceFormSubmit, ResourceFormikBridge, useResourceUploadFormLifecycle} from 'admin/resources/utils';

export const UploadResourceScorm = () => {
  const {uploadScormPackage, submitStatus} = useUploadScorm();
  const {hasPermission} = usePermissions();
  const {englishId} = useLanguages();
  const hasAdminPermission = hasPermission(SuperAndNationalAdminroles);

  usePrefetchResourceMetadataLookups();

  const initialValues = useMemo(
    () => ({
      [FormNameEnum.file]: null,
      [FormNameEnum.displayName]: null,
      [FormNameEnum.gradeLevels]: [],
      [FormNameEnum.programs]: [],
      [FormNameEnum.pillars]: [],
      [FormNameEnum.knowledgeSkillsAndAbilities]: [],
      [FormNameEnum.time]: null,
      [FormNameEnum.keyTerms]: null,
      [FormNameEnum.isAccessibleToStudents]: null,
      [FormNameEnum.briefDescription]: '',
      [FormNameEnum.competencies]: [],
      [FormNameEnum.languages]: englishId ? [englishId] : [],
      [FormNameEnum.expirationDate]: null,
      [FormNameEnum.learningObjectives]: null,
      [FormNameEnum.hasUploadPermissions]: hasAdminPermission ? true : false,
    }),
    [hasAdminPermission, englishId]
  );

  const {formKey, capturePreSubmit, registerFormik} = useResourceUploadFormLifecycle<IUploadScormFormBaseValues>(
    initialValues as IUploadScormFormBaseValues,
    submitStatus,
    'scormPackage'
  );

  const submitHandler = useCallback(
    (formValues: IUploadScormFormBaseValues, helpers: FormikHelpers<IUploadScormFormBaseValues>) => {
      capturePreSubmit(formValues);
      const payload = mapUploadResourceScorm(formValues);

      return runResourceFormSubmit(formValues, helpers, () => uploadScormPackage(payload));
    },
    [uploadScormPackage, capturePreSubmit]
  );

  return (
    <Form.Form<IUploadScormFormBaseValues>
      key={formKey}
      initialValues={initialValues as IUploadScormFormBaseValues}
      validationSchema={uploadScormFormBaseValidationSchema}
      submitHandler={submitHandler}
      enableReinitialize={false}
      disableNativeSubmit
    >
      <ResourceFormikBridge registerFormik={registerFormik} />
      <PageLayoutTwoVerticalCols
        header={<ScormContentHeader />}
        contentStart={<ScormContentStart />}
        contentMiddle={<ScormContentMiddle />}
        footer={<ScormContentFooter />}
        withDoubleFirst={false}
        withDoubleSecond
        withFooter
        withSecondaryNavigation
      />
    </Form.Form>
  );
};
